
import { AutoComplete, Button, Spin } from "antd";
import { useState, useEffect, useRef } from "react";
import { filterCities, parseLocality, parseCity } from "./locationService";

const formatCoord = (val) => Number(parseFloat(val).toFixed(6));

const NOMINATIM = "https://nominatim.openstreetmap.org";
const HEADERS = { "User-Agent": "DraptorApp/1.0" };

const LocationField = ({
  value,
  onChange,
  form,
  message,
  setAutoDetecting,
}) => {
  const [options, setOptions] = useState([]);
  const wrapperRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || "");
  const [locLoading, setLocLoading] = useState(false);

  const debounceRef = useRef(null);
  const selectedRef = useRef(false);
  const cityContextRef = useRef("");


  useEffect(() => {
    if (!selectedRef.current) {
      setInputValue(value || "");
    }
  }, [value]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target)
      ) {
        setOptions([]);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  // ─────────────────────────────────────────────────────────
  // SEARCH
  // ─────────────────────────────────────────────────────────

  const handleSearch = (val) => {
    if (val !== inputValue) {
      selectedRef.current = false;
    }

    cityContextRef.current = "";

    setInputValue(val);

    if (onChange) {
      onChange(val);
    }

    if (!val || val.length < 2) {
      setOptions([]);
      return;
    }

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setOptions([]);

      const results = await filterCities(val);

      if (results?.length) {
        setOptions(results);
      } else {
        setOptions([]);
      }
    }, 400);
  };

  // ─────────────────────────────────────────────────────────
  // FETCH LOCATION DETAILS
  // ─────────────────────────────────────────────────────────

  const fetchLocationDetails = async (
    queryString,
    cityContext = ""
  ) => {
    if (!queryString) return;

    try {
      const geocodeQuery = cityContext
        ? `${queryString}, ${cityContext}, India`
        : `${queryString}, India`;

      // FORWARD GEOCODE
      const res = await fetch(
        `${NOMINATIM}/search` +
        `?format=json` +
        `&q=${encodeURIComponent(geocodeQuery)}` +
        `&limit=1` +
        `&addressdetails=1`,
        {
          headers: HEADERS,
        }
      );

      const data = await res.json();

      if (!data.length) {
        message.error(
          "Location not found. Try selecting from suggestions."
        );
        return;
      }

      const item = data[0];

      const lat = formatCoord(item.lat);
      const lon = formatCoord(item.lon);

      // REVERSE GEOCODE
      const reverseRes = await fetch(
        `${NOMINATIM}/reverse?lat=${lat}&lon=${lon}&format=json`,
        {
          headers: HEADERS,
        }
      );

      const reverseData = await reverseRes.json();
      const address = reverseData.address || {};

      const locality = queryString;
      const city = cityContext || parseCity(address) || "";

      form.setFieldsValue({
        locality,
        city,
        state: address.state || "",
        country: address.country || "",
        pincode: address.postcode || "",
        latitude: lat,
        longitude: lon,
      });

    } catch (err) {
      console.error("[LocationField fetchDetails]", err);
      message.error("Location fetch failed. Please try again.");
    }
  };

  // ─────────────────────────────────────────────────────────
  // SELECT
  // ─────────────────────────────────────────────────────────

  const handleSelect = (val, option) => {
    selectedRef.current = true;

    cityContextRef.current = option?.city || "";

    setInputValue(val);

    if (onChange) {
      onChange(val);
    }

    fetchLocationDetails(val, option?.city || "");
  };

  // ─────────────────────────────────────────────────────────
  // BLUR
  // ─────────────────────────────────────────────────────────

  const handleBlur = () => {
    if (selectedRef.current) return;

    if (inputValue) {
      fetchLocationDetails(inputValue, "");
    }
  };

  // ─────────────────────────────────────────────────────────
  // AUTO DETECT
  // ─────────────────────────────────────────────────────────

  const handleAutoDetect = () => {
    if (locLoading) return;

    if (!navigator.geolocation) {
      message.error("Geolocation is not supported by your browser.");
      return;
    }

    setAutoDetecting(true);

    // START LOADING
    setLocLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = formatCoord(pos.coords.latitude);
        const lon = formatCoord(pos.coords.longitude);

        try {
          const controller = new AbortController();

          const timeoutId = setTimeout(() => {
            controller.abort();
          }, 5000);

          const res = await fetch(
            `${NOMINATIM}/reverse?lat=${lat}&lon=${lon}&format=json`,
            {
              headers: HEADERS,
              signal: controller.signal,
            }
          );

          clearTimeout(timeoutId);

          const data = await res.json();
          const address = data.address || {};

          const locality =
            parseLocality(address) ||
            address.suburb ||
            address.neighbourhood ||
            address.hamlet ||
            address.village ||
            address.quarter ||
            "";

          const city =
            parseCity(address) ||
            address.city ||
            address.town ||
            address.county ||
            "";

          localStorage.setItem("userCity", city);

          const shortAddress = [
            address.house_number,
            address.road,
            address.residential,
            address.pedestrian,
            address.suburb,
            address.neighbourhood,
            address.quarter,
          ]
            .filter(Boolean)
            .join(", ") ||
            data.display_name?.split(",").slice(0, 2).join(",");

          form.setFieldsValue({
            address: shortAddress,
            locality,
            city,
            state: address.state || "",
            country: address.country || "",
            pincode: address.postcode || "",
            latitude: lat,
            longitude: lon,
          });

          setInputValue(locality);

          selectedRef.current = true;

          message.destroy();

          message.success({
            content: "Location auto-filled ✅",
            duration: 1,
          });

        } catch (err) {
          console.error("[LocationField autoDetect]", err);
          message.error("Failed to fetch your location.");
        } finally {
          setLocLoading(false);
          setAutoDetecting(false);
        }
      },
      () => {
        message.error(
          "Please allow location access in your browser."
        );

        setLocLoading(false);
        setAutoDetecting(false);
      }
    );
  };

  // ─────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────

  return (
    <div
      className="csv2-location-wrapper"
      ref={wrapperRef}
    >

      <div className="csv2-location-search">

        <input
          type="text"
          className="csv2-location-input"
          value={inputValue}
          placeholder="Type locality e.g. Electronic City, Indiranagar"
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          onBlur={handleBlur}
        />

        {options.length > 0 && (
          <div className="csv2-location-dropdown">

            {options.map((item, index) => (
              <div
                key={index}
                className="csv2-location-dropdown-item"
                onMouseDown={() => {
                  handleSelect(item.value, item);
                  setOptions([]);
                }}
              >
                {item.label}
              </div>
            ))}

          </div>
        )}

      </div>

      <Button
        type="dashed"
        onClick={handleAutoDetect}
        style={{ marginTop: 10 }}
        block
        disabled={locLoading}
      >
        {locLoading && (
          <Spin
            size="small"
            style={{ marginRight: 8 }}
          />
        )}

        {locLoading
          ? "Detecting Location..."
          : "📍 Auto Detect Location"}
      </Button>

    </div>
  );
};

export default LocationField;
